import { autoUpdate } from "@floating-ui/react";
import {
	Combobox,
	ComboboxButton,
	ComboboxInput,
	ComboboxOption,
	ComboboxOptions,
	type ComboboxOptionsProps,
} from "@headlessui/react";
import { Option } from "@inrev/core";
import { rankItem, rankings } from "@tanstack/match-sorter-utils";
import {
	Fragment,
	type ReactNode,
	type UIEvent,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import type { FieldError } from "react-hook-form";
import { useLabelledBy } from "../../lib/useLabelledBy";
import { cn } from "../../lib/utils";
import { CheckboxGlyph, Icon, Text } from "../../primitives";
import { FormFieldError } from "../FormFieldError";
import { FittedLabels } from "./FittedLabels";
import { inputSize } from "./inputSize";
import { OptionBadge } from "./OptionBadge";
import { SearchField, searchFieldInputClass } from "./SearchField";

type PopoverLayout = { placement: "below" | "above"; maxHeight: number };

const popoverLayout = (rect: DOMRect, height: number | undefined): PopoverLayout => {
	const spaceBelow = window.innerHeight - rect.bottom - POPOVER_GAP_PX - POPOVER_VIEWPORT_MARGIN_PX;
	const spaceAbove = rect.top - POPOVER_GAP_PX - POPOVER_VIEWPORT_MARGIN_PX;
	if (height === undefined) {
		return { placement: "below", maxHeight: Math.max(POPOVER_MIN_HEIGHT_PX, spaceBelow) };
	}
	const placement = spaceBelow >= height || spaceBelow >= spaceAbove ? "below" : "above";
	const space = placement === "below" ? spaceBelow : spaceAbove;
	return { placement, maxHeight: Math.min(height, Math.max(POPOVER_MIN_HEIGHT_PX, space)) };
};

const samePopoverRect = (last: DOMRect, rect: DOMRect) =>
	rect.top === last.top &&
	rect.bottom === last.bottom &&
	rect.left === last.left &&
	rect.width === last.width;

const PopoverPositioner = ({
	trigger,
	floating,
	height,
	onMeasure,
}: {
	trigger: HTMLElement;
	floating: HTMLElement;
	height: number | undefined;
	onMeasure: (rect: DOMRect, layout: PopoverLayout) => void;
}) => {
	useLayoutEffect(() => {
		// Outside update so each autoUpdate frame keeps the last rect, open height, and window size.
		let last: DOMRect | undefined;
		let layout: PopoverLayout | undefined;
		let viewportWidth = window.innerWidth;
		let viewportHeight = window.innerHeight;

		const update = () => {
			const rect = trigger.getBoundingClientRect();
			const viewportChanged =
				window.innerWidth !== viewportWidth || window.innerHeight !== viewportHeight;
			if (viewportChanged) {
				viewportWidth = window.innerWidth;
				viewportHeight = window.innerHeight;
				layout = undefined;
			}
			if (!viewportChanged && last && layout && samePopoverRect(last, rect)) return;
			last = rect;
			layout ??= popoverLayout(rect, height);
			onMeasure(rect, layout);
		};

		// A collapsing header moves the field without a scroll or resize. autoUpdate follows that
		// shift. Height stays from open until the window size changes, so the panel does not stretch.
		const stop = autoUpdate(trigger, floating, update, { animationFrame: true });
		return stop;
	}, [trigger, floating, height, onMeasure]);

	return null;
};

export interface SearchableDropdownInputOption {
	label: string;
	value: string;
	searchValues?: string[];
	/**
	 * Section header. Options sharing a group render together under one header, with groups in
	 * the order their first option appears. Options without a group render first, headerless.
	 */
	group?: string;
	disabled?: boolean;
	note?: string;
	description?: string;
	/** Sits before the label, e.g. an icon. Give a decorative glyph its own screen reader text. */
	leading?: ReactNode;
	/** Second line under the label, such as a class path. */
	subtitle?: string;
	/** Short identifier shown as a pill before the label, such as a code. */
	badge?: string;
}

export interface SearchableDropdownInputFooterAction {
	label: string | ((query: string) => string);
	/** Fires with the current search text. The value is left unchanged. */
	onSelect: (query: string) => void;
	/** Set false for a text link. Defaults to the plus icon. */
	icon?: false;
}

/** A footer row that runs when picked instead of joining the selection. */
type PanelAction = { run: () => void };
type PanelItem = SearchableDropdownInputOption | PanelAction;

const isPanelAction = (item: PanelItem): item is PanelAction => "run" in item;

/**
 * Headless gives every option a selected state, so a screen reader would offer an action as a
 * pick. React leaves the attribute removed while the value it would set stays the same.
 */
const dropSelectedState = (node: HTMLElement | null) => {
	node?.removeAttribute("aria-selected");
};

// Cap mounted options so a large list doesn't render thousands of DOM nodes; the
// rest stays reachable by typing to narrow the list.
const OPTIONS_PAGE_SIZE = 50;
const LOAD_MORE_THRESHOLD_PX = 48;
const POPOVER_GAP_PX = 6;
const POPOVER_VIEWPORT_MARGIN_PX = 16;
const POPOVER_MIN_HEIGHT_PX = 240;

const toggleValue = (values: readonly string[], value: string) =>
	values.includes(value) ? values.filter((other) => other !== value) : [...values, value];

const rankOf = (target: string, query: string) => {
	const { passed, rank } = rankItem(target, query, { threshold: rankings.ACRONYM });
	return passed ? Math.min(rank, rankings.EQUAL) : 0;
};

/**
 * Orders matches so the obvious one leads: the label (exact, starts with, a word starts with,
 * contains) beats any other `searchValues` hit, and acronyms come last. Scattered-letter matches
 * are dropped. Ties keep the caller's order.
 */
const scoreOption = (option: SearchableDropdownInputOption, query: string) => {
	const labelRank = rankOf(option.label, query);
	if (labelRank >= rankings.CONTAINS) return 20 + labelRank;
	const otherRank = Math.max(
		0,
		...(option.searchValues ?? [])
			.filter((searchValue) => searchValue !== option.label)
			.map((searchValue) => rankOf(searchValue, query)),
	);
	if (otherRank >= rankings.CONTAINS) return 10 + otherRank;
	return Math.max(labelRank, otherRank);
};

export const filterOptions = (options: SearchableDropdownInputOption[], inputValue?: string) => {
	const query = inputValue?.trim();
	if (!query) return options;

	return options
		.map((option) => ({ option, score: scoreOption(option, query) }))
		.filter(({ score }) => score > 0)
		.sort((a, b) => b.score - a.score)
		.map(({ option }) => option);
};

type SharedProps = {
	disabled?: boolean;
	inputClassName?: string;
	optionClassName?: string;
	optionIconClassName?: string;
	noteClassName?: string;
	placeholder?: string;
	compact?: boolean;
	options: SearchableDropdownInputOption[];
	error?: FieldError;
	errorMessage?: string;
	readonly?: boolean;
	className?: string;
	anchor?: ComboboxOptionsProps["anchor"];
	footerAction?: SearchableDropdownInputFooterAction;
	footerHint?: string;
	/** Controls rendered above the option list, inside the open panel. */
	listHeader?: ReactNode;
	listClassName?: string;
	emptyMessage?: string;
	invalid?: boolean;
	searchPlaceholder?: string;
	/**
	 * Popover panel height in px, independent of where the button sits. The panel opens above the
	 * button when that much room isn't below it. Without it, the panel fills the room below.
	 */
	popoverHeight?: number;
	id?: string;
	"aria-labelledby"?: string;
};

export type SearchableDropdownInputSingleProps = SharedProps & {
	multiple?: false;
	value?: string;
	/** Fires with the picked option's value, or `""` when the value is cleared. */
	onChange?: (value: string) => void;
	/**
	 * `field` (default) types into the control itself. `popover` shows a button that opens a panel
	 * with the search box on top, the options in the middle and the footer action pinned below.
	 * The panel is portaled and fixed under the button, and ignores `anchor`: Headless anchors to
	 * the element that holds the search text, which sits inside the panel.
	 */
	searchPlacement?: "field" | "popover";
};

export type SearchableDropdownInputMultipleProps = SharedProps & {
	/**
	 * Picks several options from a `popover` panel that stays open; picking a checked option
	 * unchecks it. The button lists the picks in the order of `options`, as many as fit, then
	 * "+N more". `onChange` appends new picks and keeps values missing from `options`.
	 */
	multiple: true;
	value: readonly string[];
	onChange?: (value: string[]) => void;
};

export type SearchableDropdownInputProps =
	| SearchableDropdownInputSingleProps
	| SearchableDropdownInputMultipleProps;

type OptionSection = { group?: string; members: SearchableDropdownInputOption[] };

/**
 * Splits options into header sections. Without a query, ungrouped options lead and groups keep
 * the order of their first option. With a query the options arrive ranked, so every section
 * (ungrouped included) takes the place of its best match.
 */
const groupOptions = (options: SearchableDropdownInputOption[], ranked: boolean) => {
	const sections = new Map<string | undefined, OptionSection>();
	if (!ranked) sections.set(undefined, { members: [] });
	for (const option of options) {
		const section = sections.get(option.group);
		if (section) section.members.push(option);
		else sections.set(option.group, { group: option.group, members: [option] });
	}
	return [...sections.values()].filter((section) => section.members.length > 0);
};

export const SearchableDropdownInput = (props: SearchableDropdownInputProps) => {
	const {
		inputClassName,
		optionClassName,
		optionIconClassName,
		noteClassName,
		placeholder,
		compact,
		options,
		error,
		errorMessage,
		disabled,
		readonly,
		className,
		anchor,
		footerAction,
		footerHint,
		listHeader,
		listClassName,
		emptyMessage,
		invalid = false,
		searchPlaceholder,
		popoverHeight,
		id,
		"aria-labelledby": ariaLabelledBy,
	} = props;
	const multiple = props.multiple === true;
	const popover = multiple || props.searchPlacement === "popover";
	const [trigger, setTrigger] = useState<HTMLElement | null>(null);
	const [floating, setFloating] = useState<HTMLElement | null>(null);
	const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);
	const [layout, setLayout] = useState<PopoverLayout | null>(null);
	const handleTriggerMeasure = useCallback((rect: DOMRect, next: PopoverLayout) => {
		setTriggerRect(rect);
		setLayout(next);
	}, []);
	const setFloatingNode = useCallback((node: HTMLElement | null) => {
		setFloating(node);
		if (!node) {
			setTriggerRect(null);
			setLayout(null);
		}
	}, []);
	const { affordanceClearance, affordanceInset, frame, paddingX, text } = inputSize(compact);
	const [query, setQuery] = useState<string>("");
	const inputRef = useRef<HTMLInputElement>(null);
	const triggerRef = useLabelledBy<HTMLButtonElement>(ariaLabelledBy, setTrigger);
	const fieldInputRef = useLabelledBy<HTMLInputElement>(ariaLabelledBy, inputRef);
	const values = useMemo<readonly string[]>(
		() => (props.multiple ? props.value : props.value ? [props.value] : []),
		[props.multiple, props.value],
	);
	const commit = (next: string[]) =>
		props.multiple ? props.onChange?.(next) : props.onChange?.(next[0] ?? "");

	const selectedValues = useMemo(() => new Set(values), [values]);
	const selectedOptions = useMemo(
		() => options.filter((option) => selectedValues.has(option.value)),
		[options, selectedValues],
	);
	const selectedLabels = useMemo(
		() => selectedOptions.map((option) => option.label),
		[selectedOptions],
	);
	const lockedValues = new Set(
		selectedOptions.filter((option) => option.disabled).map((option) => option.value),
	);
	const keptOnClear = values.filter((value) => lockedValues.has(value));

	const singleValue = props.multiple ? undefined : props.value;
	const prevValueRef = useRef(singleValue);
	useEffect(() => {
		if (prevValueRef.current !== singleValue) {
			setQuery("");
			prevValueRef.current = singleValue;
		}
	}, [singleValue]);

	// Headless UI pins ComboboxButton to tabIndex -1, which leaves the popover trigger unreachable by Tab.
	useLayoutEffect(() => {
		trigger?.setAttribute("tabindex", "0");
	});

	const filteredOptions = useMemo(
		() => (query === "" ? options : filterOptions(options, query)),
		[query, options],
	);
	const [page, setPage] = useState({ source: filteredOptions, limit: OPTIONS_PAGE_SIZE });
	const renderLimit = page.source === filteredOptions ? page.limit : OPTIONS_PAGE_SIZE;
	const visibleOptions = useMemo(
		() => filteredOptions.slice(0, renderLimit),
		[filteredOptions, renderLimit],
	);
	const sections = useMemo(
		() => groupOptions(visibleOptions, query.trim() !== ""),
		[visibleOptions, query],
	);

	const handleListScroll = (event: UIEvent<HTMLDivElement>) => {
		if (renderLimit >= filteredOptions.length) return;
		const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
		if (scrollTop + clientHeight >= scrollHeight - LOAD_MORE_THRESHOLD_PX) {
			setPage({ source: filteredOptions, limit: renderLimit + OPTIONS_PAGE_SIZE });
		}
	};

	const renderOption = (option: SearchableDropdownInputOption) => {
		const selected = selectedValues.has(option.value);
		return (
			<ComboboxOption
				key={option.value}
				value={option}
				disabled={readonly || option.disabled}
				className={cn(
					"flex items-center text-primary w-full py-[6px] space-x-[10px] data-[focus]:bg-surface-neutral hover:bg-surface-neutral",
					paddingX,
					option.group !== undefined && Option.isNone(option.leading) && "pl-6",
					"pr-[10px]",
					readonly && "cursor-default",
					option.disabled &&
						(popover
							? "cursor-not-allowed text-subtle hover:bg-transparent"
							: "cursor-not-allowed opacity-60 hover:bg-transparent"),
					optionClassName,
				)}
			>
				{multiple && <CheckboxGlyph size="sm" checked={selected} disabled={option.disabled} />}
				{Option.isSome(option.leading) && (
					<span className="flex shrink-0 items-center" data-testid="dropdown-option-leading">
						{option.leading}
					</span>
				)}
				{option.badge && <OptionBadge badge={option.badge} />}
				<div className="flex min-w-0 flex-1 flex-col gap-1" data-testid="dropdown-option">
					<span>{option.label}</span>
					{option.description && (
						<Text
							type="caption"
							color="tertiary"
							className="font-normal"
							truncate
							data-testid="dropdown-option-description"
						>
							{option.description}
						</Text>
					)}
					{option.subtitle && (
						<Text type="caption" color="tertiary" data-testid="dropdown-option-subtitle">
							{option.subtitle}
						</Text>
					)}
				</div>
				{option.note && (
					<Text
						type="caption"
						color="tertiary"
						className={cn("shrink-0", noteClassName)}
						data-testid="dropdown-option-note"
					>
						{option.note}
					</Text>
				)}
				{!multiple &&
					(selected ? (
						<Icon
							type="check"
							size="mini"
							color="secondary"
							filled
							className={optionIconClassName}
							data-testid="dropdown-option-check"
						/>
					) : (
						<div className="w-[16px] h-[16px]" />
					))}
			</ComboboxOption>
		);
	};

	const clearSelectionAction: PanelAction = { run: () => commit(keptOnClear) };

	const footerRowAction: PanelAction = {
		run: () => {
			footerAction?.onSelect(query);
			setQuery("");
			// Headless keeps a multiple combobox open after a pick and has no close(), so press Escape for it.
			if (multiple) {
				inputRef.current?.dispatchEvent(
					new KeyboardEvent("keydown", { key: "Escape", bubbles: true }),
				);
			}
		},
	};

	const handleSelectionChange = (selection: PanelItem | (PanelItem | null)[] | null) => {
		const items = Option.filterNone([selection].flat());
		const action = items.find(isPanelAction);
		if (action) {
			action.run();
			return;
		}
		const picked = items.filter(
			(item): item is SearchableDropdownInputOption => !isPanelAction(item),
		);
		if (multiple) {
			// Headless rebuilds the array from `selectedOptions`, which drops values missing from
			// `options` and reorders the rest, so apply only the toggled option to `value`. The panel
			// stays open, so the search stays too for picking a neighbor.
			const toggled =
				picked.find((option) => !selectedValues.has(option.value)) ??
				selectedOptions.find((option) => !picked.includes(option));
			if (toggled) commit(toggleValue(values, toggled.value));
			return;
		}
		setQuery("");
		commit(picked.map((option) => option.value));
	};

	const handleClearValue = (e: React.MouseEvent) => {
		e.stopPropagation();
		setQuery("");
		commit([]);
		inputRef.current?.focus();
	};

	const showClearButton = !popover && values.length > 0 && !readonly && !disabled;
	const showDropdownIcon = !showClearButton && !readonly;
	const invalidOutline =
		(error || invalid) && "outline-danger hover:outline-danger focus-visible:outline-danger";

	const listContent = (
		<>
			{sections.map(({ group, members }) =>
				group === undefined ? (
					<Fragment key="__ungrouped__">{members.map(renderOption)}</Fragment>
				) : (
					<div
						key={group}
						role="group"
						aria-label={group}
						className="group/section mt-1 first:mt-0"
					>
						<Text
							type="caption"
							color="secondary"
							className="sticky top-0 z-[1] block cursor-default border-y border-bc-default bg-surface-neutral px-3 py-1.5 font-semibold group-first/section:border-t-0"
							data-testid="dropdown-group-header"
						>
							{group}
						</Text>
						{members.map(renderOption)}
					</div>
				),
			)}
			{visibleOptions.length === 0 && emptyMessage && (
				<Text
					type="caption"
					color="tertiary"
					className="block cursor-default select-none px-[10px] py-[6px]"
					data-testid="dropdown-empty"
				>
					{emptyMessage}
				</Text>
			)}
		</>
	);

	const showClearSelection = multiple && keptOnClear.length < values.length;
	const footerActionOption = footerAction && (
		<ComboboxOption
			ref={dropSelectedState}
			value={footerRowAction}
			disabled={readonly}
			className={cn(
				"flex items-center gap-2 rounded px-2 font-medium text-link data-[focus]:bg-surface-accent hover:bg-surface-accent",
				footerHint ? "ml-auto py-1" : "py-2",
			)}
			data-testid="dropdown-footer-action"
		>
			{footerAction.icon !== false && <Icon type="plus" size="mini" aria-hidden />}
			{typeof footerAction.label === "function" ? footerAction.label(query) : footerAction.label}
		</ComboboxOption>
	);
	const footer = footerHint ? (
		<div className="flex shrink-0 items-center justify-between gap-3 border-t border-bc-default px-3 py-2">
			<Text type="caption" color="tertiary" className="shrink-0">
				{footerHint}
			</Text>
			{footerActionOption}
		</div>
	) : (
		(footerAction || showClearSelection) && (
			<div className="shrink-0 border-t border-bc-default p-1">
				{showClearSelection && (
					<ComboboxOption
						ref={dropSelectedState}
						value={clearSelectionAction}
						disabled={readonly}
						className="flex items-center gap-2 rounded px-2 py-2 text-secondary data-[focus]:bg-surface-neutral hover:bg-surface-neutral"
						data-testid="dropdown-clear-selection"
					>
						<Icon type="x-mark" size="mini" aria-hidden />
						Clear selection
					</ComboboxOption>
				)}
				{footerActionOption}
			</div>
		)
	);

	return (
		<div className={cn("relative h-fit", className)} data-testid="searchable-input-with-dropdown">
			<Combobox<PanelItem | null, boolean>
				multiple={multiple}
				disabled={disabled || readonly}
				value={multiple ? selectedOptions : (selectedOptions[0] ?? null)}
				onChange={handleSelectionChange}
				onClose={() => setQuery("")}
				immediate
			>
				{({ open }) => (
					<div className="relative h-fit">
						{open && popover && trigger && floating && (
							<PopoverPositioner
								trigger={trigger}
								floating={floating}
								height={popoverHeight}
								onMeasure={handleTriggerMeasure}
							/>
						)}
						{popover ? (
							<ComboboxButton
								id={id}
								data-testid="searchable-trigger"
								ref={triggerRef}
								disabled={disabled || readonly}
								className={cn(
									"flex w-full items-center justify-between gap-2 text-left rounded-md bg-surface-light/70 outline outline-[1px] outline-offset-[-1px] outline-bc-strong data-[open]:outline-bc-focus disabled:cursor-not-allowed disabled:opacity-50",
									frame,
									paddingX,
									!disabled && !readonly && "hover:outline-bc-stronger",
									readonly && "bg-surface-dark/70 opacity-100 outline-[0px]",
									invalidOutline,
									inputClassName,
								)}
							>
								{multiple && selectedLabels.length > 0 ? (
									<FittedLabels labels={selectedLabels} className="text-primary" />
								) : (
									<span className="flex min-w-0 flex-1 items-center gap-2">
										{selectedOptions[0]?.badge && <OptionBadge badge={selectedOptions[0].badge} />}
										<Text
											truncate
											color={selectedLabels.length > 0 ? "primary" : "tertiary"}
											className="min-w-0"
										>
											{selectedLabels[0] ?? placeholder}
										</Text>
									</span>
								)}
								{!readonly && (
									<Icon type="chevron-down" size="mini" color="tertiary" className="shrink-0" />
								)}
							</ComboboxButton>
						) : (
							<ComboboxInput
								ref={fieldInputRef}
								id={id}
								displayValue={() => selectedLabels[0] ?? ""}
								onChange={(e) => setQuery(e.target.value)}
								placeholder={placeholder}
								disabled={disabled}
								readOnly={readonly}
								data-1p-ignore
								data-testid="searchable-input"
								className={cn(
									"w-full text-left text-primary rounded-md bg-surface-light/70 outline outline-[1px] outline-offset-[-1px] outline-bc-strong focus-visible:outline-bc-heavy focus-visible:hover:outline-bc-heavy focus-visible:outline-[2px] disabled:cursor-not-allowed disabled:opacity-50",
									frame,
									paddingX,
									!disabled && !readonly && "hover:outline-bc-stronger",
									readonly && "bg-surface-dark/70 opacity-100 outline-[0px]",
									(showClearButton || showDropdownIcon) && affordanceClearance,
									invalidOutline,
									inputClassName,
								)}
							/>
						)}

						{showClearButton && (
							<button
								type="button"
								onClick={handleClearValue}
								className={cn(
									"absolute top-1/2 transform -translate-y-1/2 text-tertiary hover:text-secondary",
									affordanceInset,
								)}
								data-testid="clear-button"
								tabIndex={-1}
							>
								<Icon type="x-mark" size="mini" />
							</button>
						)}

						{!popover && showDropdownIcon && (
							<ComboboxButton
								className={cn(
									"absolute top-1/2 transform -translate-y-1/2 text-tertiary cursor-pointer",
									affordanceInset,
								)}
								data-testid="dropdown-button"
							>
								<Icon type="chevron-down" size="mini" />
							</ComboboxButton>
						)}

						<ComboboxOptions
							ref={setFloatingNode}
							anchor={popover ? undefined : anchor}
							portal={popover}
							modal={!popover}
							onWheel={(event) => event.stopPropagation()}
							style={
								popover && triggerRect && layout
									? {
											...(layout.placement === "below"
												? { top: triggerRect.bottom + POPOVER_GAP_PX }
												: {
														bottom: window.innerHeight - triggerRect.top + POPOVER_GAP_PX,
														height: layout.maxHeight,
													}),
											left: triggerRect.left,
											width: triggerRect.width,
											maxHeight: layout.maxHeight,
										}
									: undefined
							}
							className={cn(
								"flex flex-col overflow-hidden px-0 text-secondary font-normal rounded bg-surface-base shadow-lg z-[5000] cursor-pointer select-none outline outline-[1px] outline-bc-subtle outline-offset-[-1px]",
								!popover && "max-h-60",
								text,
								popover
									? "fixed z-[200001] shadow-panel-menu"
									: anchor
										? "w-[var(--input-width)] z-[200001] [--anchor-gap:5px]"
										: "absolute w-full top-full mt-[5px]",
							)}
							data-testid="options-list"
						>
							{popover && (
								<SearchField className="shrink-0 border-b border-bc-subtle px-3">
									<ComboboxInput
										ref={inputRef}
										autoFocus
										aria-label={searchPlaceholder ?? placeholder}
										displayValue={() => ""}
										onChange={(e) => setQuery(e.target.value)}
										placeholder={searchPlaceholder}
										data-1p-ignore
										data-testid="searchable-input"
										className={cn(searchFieldInputClass, "w-full py-[10px]", text)}
									/>
								</SearchField>
							)}
							{listHeader && (
								<div
									className="shrink-0 border-b border-bc-subtle px-3 py-2"
									data-testid="dropdown-list-header"
									onMouseDown={(event) => event.preventDefault()}
									onClick={() => inputRef.current?.focus()}
								>
									{listHeader}
								</div>
							)}
							<div
								className={cn(
									"min-h-0 flex-1 overflow-y-auto overscroll-y-contain",
									popover && "pb-1",
									listClassName,
								)}
								onScroll={handleListScroll}
								data-testid="options-scroll"
							>
								{listContent}
							</div>
							{footer}
						</ComboboxOptions>
					</div>
				)}
			</Combobox>
			<FormFieldError error={error} errorMessage={errorMessage} />
		</div>
	);
};
